import React, { KeyboardEvent, useRef } from "react";
import {
  Segment,
  Track,
  V3SegmentedControlSize,
} from "./SegmentedControl.styled";

export type { V3SegmentedControlSize };

export interface V3SegmentedControlOption<TValue extends string> {
  label: string;
  value: TValue;
  disabled?: boolean;
}

export interface V3SegmentedControlProps<TValue extends string> {
  options: V3SegmentedControlOption<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  size?: V3SegmentedControlSize;
  fullWidth?: boolean;
  /** Accessible name for the group, e.g. "Withdrawal amount". */
  label?: string;
  testID?: string;
}

const NEXT_KEYS = ["ArrowRight", "ArrowDown"];
const PREVIOUS_KEYS = ["ArrowLeft", "ArrowUp"];

export const V3SegmentedControl = <TValue extends string>({
  options,
  value,
  onChange,
  size = "md",
  fullWidth = false,
  label,
  testID,
}: V3SegmentedControlProps<TValue>): JSX.Element => {
  const segmentRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const enabledIndexes = options.reduce<number[]>((indexes, option, index) => {
    if (!option.disabled) indexes.push(index);
    return indexes;
  }, []);

  const selectedIndex = options.findIndex((option) => option.value === value);
  const focusableIndex =
    selectedIndex >= 0 ? selectedIndex : enabledIndexes[0] ?? -1;

  const move = (from: number, step: 1 | -1) => {
    if (enabledIndexes.length === 0) return;
    const position = enabledIndexes.indexOf(from);
    const next =
      enabledIndexes[
        (position + step + enabledIndexes.length) % enabledIndexes.length
      ];
    onChange(options[next].value);
    segmentRefs.current[next]?.focus();
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (NEXT_KEYS.includes(event.key)) {
      event.preventDefault();
      move(index, 1);
      return;
    }
    if (PREVIOUS_KEYS.includes(event.key)) {
      event.preventDefault();
      move(index, -1);
    }
  };

  return (
    <Track
      role="radiogroup"
      aria-label={label}
      $fullWidth={fullWidth}
      data-testid={testID}
    >
      {options.map((option, index) => (
        <Segment
          key={option.value}
          ref={(node) => {
            segmentRefs.current[index] = node;
          }}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          disabled={option.disabled}
          tabIndex={index === focusableIndex ? 0 : -1}
          onClick={() => onChange(option.value)}
          onKeyDown={(event) => handleKeyDown(event, index)}
          data-testid={testID ? `${testID}-${option.value}` : undefined}
          $selected={option.value === value}
          $size={size}
          $fullWidth={fullWidth}
        >
          {option.label}
        </Segment>
      ))}
    </Track>
  );
};
